/**
 * App-shell sidebar — vertical primary navigation for the portal.
 *
 * Three layouts, one component:
 * - **Mobile (< md)**: hidden by default; opens as an off-canvas drawer via
 *   the topbar hamburger. Width auto-expanded to ~280px.
 * - **Tablet/desktop collapsed**: 64px icon-only rail with hover tooltips.
 * - **Desktop expanded**: 240px with icons + labels and section headers.
 *
 * The collapsed/expanded preference persists to `localStorage` so users get
 * the same layout next visit. Sections are derived from the nav item's href
 * via `SECTION_FOR_HREF` so the legacy `navigation: { name, href }[]` shape
 * keeps working unchanged for tests and existing callers.
 *
 * @changeHistory
 * - 2026-05-09: Initial creation - app shell sidebar (Claude)
 * - 2026-05-17: Fixed incorrect logo color, based off UI color scheme. (Neer Patel)
 * - 2026-05-18: Preserved existing logo-color mapping per product direction (Codex)
 */

import {
  Activity,
  BarChart3,
  Briefcase,
  Calculator,
  ChevronLeft,
  ChevronRight,
  FileSignature,
  Home,
  LayoutDashboard,
  LogOut,
  Newspaper,
  Repeat,
  Settings,
  ShieldCheck,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useState, type JSX, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { cn } from '../lib/utils';
import { useAuthStore } from '../stores/auth.store';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';

export interface NavItem {
  name: string;
  href: string;
}

interface AppSidebarProps {
  navigation: NavItem[];
  /** When true, the sidebar is rendered inside the mobile drawer (no fixed
   *  positioning, always-expanded label mode, no collapse toggle). */
  variant?: 'fixed' | 'drawer';
  /** Called when a nav link is clicked. Useful for the mobile drawer so it
   *  can close itself when the user navigates. */
  onNavigate?: () => void;
  /** Optional logout handler — falls back to clearing the auth store. */
  onLogout?: () => void;
}

const COLLAPSE_STORAGE_KEY = 'cda-portal:sidebar-collapsed';

const ICON_FOR_HREF: Record<string, LucideIcon> = {
  '/dashboard': LayoutDashboard,
  '/submit': FileSignature,
  '/transactions': Repeat,
  '/team': Users,
  '/listings': Home,
  '/blog-posts': Newspaper,
  '/title-companies': Briefcase,
  '/reporting': BarChart3,
  '/compensation-plans': Calculator,
  '/audit-logs': ShieldCheck,
  '/admin/diagnostics': Activity,
};

/** Heading each href falls under in the sidebar. Ungrouped items get a
 *  fallback header `Workspace`. */
const SECTION_FOR_HREF: Record<string, string> = {
  '/dashboard': 'Work',
  '/submit': 'Work',
  '/transactions': 'Work',
  '/team': 'Work',
  '/listings': 'Work',
  '/blog-posts': 'Library',
  '/title-companies': 'Work',
  '/reporting': 'Admin',
  '/compensation-plans': 'Admin',
  '/audit-logs': 'Admin',
  '/admin/diagnostics': 'Admin',
};

const SECTION_ORDER = ['Work', 'Admin', 'Library', 'Workspace'];

const groupBySection = (items: NavItem[]): Array<{ section: string; items: NavItem[] }> => {
  const groups = new Map<string, NavItem[]>();
  items.forEach((item) => {
    const section = SECTION_FOR_HREF[item.href] ?? 'Workspace';
    const bucket = groups.get(section) ?? [];
    bucket.push(item);
    groups.set(section, bucket);
  });

  return SECTION_ORDER.flatMap((section) => {
    const bucket = groups.get(section);
    if (!bucket || bucket.length === 0) {
      return [];
    }
    return [{ section, items: bucket }];
  });
};

const isActivePath = (currentPath: string, href: string): boolean => {
  if (currentPath === href) {
    return true;
  }
  // Treat any deeper sub-route as active for that nav item (e.g. /listings/123
  // highlights "Listings"). Guard against root href '/' which would match
  // everything.
  return href !== '/' && currentPath.startsWith(`${href}/`);
};

const initials = (firstName?: string, lastName?: string): string =>
  `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase() || 'U';

const SidebarLink = ({
  item,
  active,
  collapsed,
  onClick,
}: {
  item: NavItem;
  active: boolean;
  collapsed: boolean;
  onClick?: () => void;
}): JSX.Element => {
  const Icon = ICON_FOR_HREF[item.href] ?? LayoutDashboard;

  return (
    <Link
      to={item.href}
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      title={collapsed ? item.name : undefined}
      className={cn(
        'group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
        active
          ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-200'
          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800/70 dark:hover:text-slate-100',
        collapsed && 'justify-center px-2'
      )}
    >
      {/* Active indicator bar on the left edge */}
      {active && !collapsed && (
        <span
          aria-hidden="true"
          className="absolute left-0 top-1/2 h-6 w-0.5 -translate-y-1/2 rounded-r-full bg-blue-600 dark:bg-blue-400"
        />
      )}
      <Icon
        className={cn('h-5 w-5 shrink-0', active && 'text-blue-600 dark:text-blue-300')}
        aria-hidden="true"
      />
      {!collapsed && <span className="truncate">{item.name}</span>}
      {collapsed && (
        <span className="pointer-events-none absolute left-full ml-2 hidden whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-xs font-medium text-white shadow-lg group-hover:block dark:bg-slate-700">
          {item.name}
        </span>
      )}
    </Link>
  );
};

const SectionHeader = ({ label }: { label: string }): JSX.Element => (
  <p className="px-3 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
    {label}
  </p>
);

const SidebarLogo = ({ collapsed }: { collapsed: boolean }): JSX.Element => {
  const logoBlue = '/api/v1/assets/logos/blue';
  const logoWhite = '/api/v1/assets/logos/white';
  return (
    <div className={cn('flex items-center', collapsed ? 'justify-center px-2' : 'px-4')}>
      {collapsed ? (
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-600 text-sm font-bold text-white shadow-sm dark:bg-blue-500">
          CT
        </span>
      ) : (
        <>
          <img
            src={logoWhite}
            alt="Cloud Team Realty"
            className="block h-32 w-auto object-contain dark:hidden"
          />
          <img
            src={logoBlue}
            alt="Cloud Team Realty"
            className="hidden h-32 w-auto object-contain dark:block"
          />
        </>
      )}
    </div>
  );
};

const UserFooter = ({
  collapsed,
  onLogout,
}: {
  collapsed: boolean;
  onLogout?: () => void;
}): JSX.Element => {
  const { user, logout } = useAuthStore();
  const handleLogout = onLogout ?? logout;

  if (collapsed) {
    return (
      <div className="flex flex-col items-center gap-2 px-2 py-3">
        <Link
          to="/profile"
          title="Profile"
          className="rounded-full transition hover:ring-2 hover:ring-blue-300 dark:hover:ring-blue-700"
        >
          <Avatar className="h-9 w-9">
            <AvatarImage src={user?.photoUrl} />
            <AvatarFallback>{initials(user?.firstName, user?.lastName)}</AvatarFallback>
          </Avatar>
        </Link>
        <button
          type="button"
          onClick={handleLogout}
          title="Log out"
          aria-label="Log out"
          className="flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-red-600 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-red-400"
        >
          <LogOut className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2 px-3 py-3">
      <Link
        to="/profile"
        className="flex items-center gap-3 rounded-lg px-2 py-2 transition hover:bg-slate-100 dark:hover:bg-slate-800/70"
      >
        <Avatar className="h-9 w-9">
          <AvatarImage src={user?.photoUrl} />
          <AvatarFallback>{initials(user?.firstName, user?.lastName)}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
            {user?.fullName ?? 'Account'}
          </p>
          <p className="truncate text-xs capitalize text-slate-500 dark:text-slate-400">
            {user?.role?.replace(/_/g, ' ') ?? ''}
          </p>
        </div>
      </Link>
      <div className="flex items-center gap-2">
        <Link
          to="/profile"
          className="flex flex-1 items-center justify-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
        >
          <Settings className="h-3.5 w-3.5" aria-hidden="true" />
          Settings
        </Link>
        <button
          type="button"
          onClick={handleLogout}
          className="flex items-center justify-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:border-red-200 hover:bg-red-50 hover:text-red-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:border-red-900/60 dark:hover:bg-red-950/30 dark:hover:text-red-300"
          aria-label="Log out"
        >
          <LogOut className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

const AppSidebar = ({
  navigation,
  variant = 'fixed',
  onNavigate,
  onLogout,
}: AppSidebarProps): JSX.Element => {
  const location = useLocation();
  const isDrawer = variant === 'drawer';
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (isDrawer || typeof window === 'undefined') {
      return;
    }

    try {
      const stored = window.localStorage.getItem(COLLAPSE_STORAGE_KEY);
      if (stored === '1') {
        setCollapsed(true);
      }
    } catch {
      // localStorage unavailable (private mode, etc) — leave default expanded.
    }
  }, [isDrawer]);

  const toggleCollapsed = (): void => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(COLLAPSE_STORAGE_KEY, next ? '1' : '0');
        }
      } catch {
        // ignore storage failures
      }
      return next;
    });
  };

  // The drawer mode never collapses to icon-only — it's already a temporary
  // surface that the user expects to dismiss after navigating.
  const isCollapsed = isDrawer ? false : collapsed;
  const grouped = groupBySection(navigation);

  const wrapperClasses = isDrawer
    ? 'flex h-full w-full flex-col bg-white dark:bg-slate-900'
    : cn(
        'sticky top-0 hidden h-screen shrink-0 flex-col border-r border-slate-200 bg-white transition-[width] duration-200 ease-out dark:border-slate-800 dark:bg-slate-900 md:flex',
        isCollapsed ? 'w-16' : 'w-60'
      );

  const content: ReactNode = (
    <>
      <div className="flex items-center justify-between py-4">
        <SidebarLogo collapsed={isCollapsed} />
        {!isDrawer && (
          <button
            type="button"
            onClick={toggleCollapsed}
            aria-label={isCollapsed ? 'Expand navigation' : 'Collapse navigation'}
            className={cn(
              'mr-2 flex h-7 w-7 items-center justify-center rounded-md text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200',
              isCollapsed && 'mr-0 ml-0'
            )}
          >
            {isCollapsed ? (
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            ) : (
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            )}
          </button>
        )}
      </div>

      <nav
        aria-label="Primary"
        data-testid="layout-navigation"
        className="flex-1 overflow-y-auto px-2 pb-4"
      >
        {grouped.map(({ section, items }) => (
          <div key={section} className="mb-1">
            {!isCollapsed && <SectionHeader label={section} />}
            {isCollapsed && grouped.length > 1 && (
              <div
                aria-hidden="true"
                className="my-2 border-t border-slate-100 dark:border-slate-800"
              />
            )}
            <ul className="space-y-0.5">
              {items.map((item) => (
                <li key={item.href}>
                  <SidebarLink
                    item={item}
                    active={isActivePath(location.pathname, item.href)}
                    collapsed={isCollapsed}
                    onClick={onNavigate}
                  />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      <div className="border-t border-slate-100 dark:border-slate-800">
        <UserFooter collapsed={isCollapsed} onLogout={onLogout} />
      </div>
    </>
  );

  return (
    <aside aria-label="Primary navigation" className={wrapperClasses}>
      {content}
    </aside>
  );
};

export default AppSidebar;
